/**
 * ⭐ Supplements › Today, as data (step 2 of the Supplements pass). Pure: the day tray, the
 * day's list and the side panel all read this, so they can never disagree about a count.
 *
 * The rules it holds:
 *  · Only what is DUE today is listed; anything active but not due today is a quiet
 *    "Not due today" line with when it is due next. ⭐ A dose marked before its item was paused
 *    today stays, as marked (`countedDoses` — a dose taken is a dose taken); its open ones go.
 *  · Counts are honest: taken, skipped and still open are three numbers; a skip is never
 *    taken; a one-off dose is listed but counts for nothing but supply.
 *  · NEXT is the first dose not yet recorded, in the day's order (an earlier open dose before
 *    a later one) — the only dose that gets the solid "Taken" button.
 *  · NOW is the part of the person's day the clock is in. It only marks a compartment; nothing
 *    fills or sweeps by the clock.
 *  · TO GO splits by NOW: "to take" is open in the NOW compartment or an earlier one (the tray's
 *    corner labels' rule), "later" is open in a later one.
 *  · Every clock follows the app's 12/24-hour setting, passed in (`fmt`) — never read here.
 */
import {
  amountOn, clockRange, clockText, countedDoses, dayDoses, doseText, frequencyLabel, isDaily, isExtraKey, isRunningLow, markOf,
  nextChange, nextDueOn, rateText, reviewDue, runsOutOn, supplyLeft, takenCount, skippedCount, timeLabel, timeOfDayOf,
  timePhrase, toHHMM, daysBetween,
  DEFAULT_DAY_TIMES, EXTRA_MARK, TIMES_OF_DAY, SKIP_PREFIX,
  type AmountChange, type DayTimes, type Dose, type DoseMark, type TimeOfDay, type UnitForm,
} from '../../../utils/intakeSchedule';
import type { Supplement, SupplementDay } from '../../../utils/supplements';
import type { TimeFormat } from '../../../utils/time';
import { shiftYMD } from '../../../utils/hydration';
import { dayLabel } from './supplementsFormat';

/** The drawn shape of a pill: from the unit, else from the item's icon. */
export type PillShape = 'softgel' | 'capsule' | 'tablet' | 'scoop' | 'drop' | 'gummy' | 'sachet';

export function pillShapeOf(item: Pick<Supplement, 'form' | 'icon'>): PillShape {
  const form: UnitForm | null | undefined = item.form;
  if (form === 'softgel' || form === 'capsule' || form === 'tablet' || form === 'scoop' || form === 'gummy' || form === 'sachet') return form;
  if (form === 'drop' || form === 'ml') return 'drop';
  switch (item.icon) {
    case 'tablets': return 'tablet';
    case 'droplet': case 'flask': return 'drop';
    case 'fish': case 'sun': return 'softgel';
    case 'leaf': case 'citrus': case 'coffee': return 'tablet';
    case 'dumbbell': return 'scoop';
    default: return 'capsule';
  }
}

export interface TodayDose {
  dose: Dose;
  item: Supplement;
  mark: DoseMark;
  isNext: boolean;
  /** "With lunch", "30 min before breakfast". */
  when: string;
  /** "2 softgels · 1,000 mg each" (or the item's old dose text). */
  amount: string;
  /** The item's note ("Not with coffee or tea"), if any. */
  note: string | null;
  /** The second dose of the day and on: "2nd dose". */
  nth: string | null;
  shape: PillShape;
  runningLow: boolean;
}

export interface TodayGroup {
  timeOfDay: TimeOfDay;
  label: string;
  doses: TodayDose[];
  taken: number;
  skipped: number;
  /** Not yet recorded. */
  open: number;
  /** Has doses and every one is recorded. */
  done: boolean;
  isNow: boolean;
  /** "With lunch · 1:00 PM" when the group shares one anchor, else its clock range "7:00–7:30 AM". */
  hint: string;
}

export interface NotDueItem { item: Supplement; how: string; next: string | null }
/** A one-off dose ("ALSO TAKEN"): its record key, the item, its pill and "1 tablet · 25 mg each". */
export interface ExtraDose { key: string; item: Supplement; shape: PillShape; amount: string }

/**
 * When NEXT is due, in words (the readout's "NEXT · 1:00 PM", the row's tag, the widget's tile).
 *  · `beforeItsTime` — its time of day is later than NOW (D4: its Taken is outlined until then).
 *  · `fromEarlier` — its time of day is earlier than NOW: it has been waiting ("From this morning").
 */
export interface NextTiming {
  /** "1:00 PM" — where it falls on the person's day; null for a plain time of day (no clock was set). */
  clock: string | null;
  beforeItsTime: boolean;
  fromEarlier: boolean;
  /** The tag after "NEXT · ": "From this morning" when it's waiting, else its clock, else its time of day ("Morning"). */
  tag: string;
  /** Its time inside a line: "with any meal", "7:00 PM", or "" for a plain time of day. */
  phrase: string;
}

/** The first dose of the next day anything is due (the readout when nothing is open today). */
export interface UpcomingDose {
  date: string;
  isTomorrow: boolean;
  /** "Tomorrow", or the day ("Sat 24"). */
  day: string;
  dose: Dose;
  item: Supplement;
  /** "7:00 AM"; null for a plain time of day — then `part` says it. */
  clock: string | null;
  /** Its time of day: "Morning". */
  part: string;
}

/** One dose in TOMORROW's line: "Probiotic 7:00 AM", "Vitamin D3 with breakfast". */
export interface TomorrowEntry {
  dose: Dose;
  item: Supplement;
  /** "7:00 AM", "with breakfast", or "" for a plain time of day (the line names it). */
  when: string;
  /** "2nd dose" on the item's second time that day and on. */
  nth: string | null;
}
export interface TomorrowLine { timeOfDay: TimeOfDay; label: string; entries: TomorrowEntry[] }
/** TOMORROW: what the schedule has due the day after `date` — read only, nothing to mark. */
export interface TomorrowModel {
  date: string;
  /** Doses due tomorrow. */
  due: number;
  /** One line per time of day that has doses, in the day's order. */
  lines: TomorrowLine[];
  /** Active items not due tomorrow, with the day each is next due (null: never again — it ended). */
  notDue: NotDueItem[];
}

/** open: doses still to record · allRecorded: every due dose recorded · nothingDue: nothing due today. */
export type TodayStatus = 'open' | 'allRecorded' | 'nothingDue';

export interface TodayModel {
  date: string;
  groups: TodayGroup[];
  due: number;
  taken: number;
  skipped: number;
  open: number;
  /** Open in the NOW compartment or an earlier one — "2 to take". */
  toTake: number;
  /** Open in a later compartment — "2 later". toTake + later = open. */
  later: number;
  next: TodayDose | null;
  /** NEXT's timing words (null with no NEXT). */
  nextTiming: NextTiming | null;
  status: TodayStatus;
  /** With nothing open today: the next due day's first dose ("NEXT · TOMORROW 7:00 AM · Probiotic"). Null while doses are open, or nothing is due within a year. */
  upcoming: UpcomingDose | null;
  tomorrow: TomorrowModel;
  now: TimeOfDay;
  notDue: NotDueItem[];
  /** Paused items (the list's quiet "Paused: Iron · Resume"); since when: `pausedSince` (utils/intakeSchedule). */
  paused: Supplement[];
  /** Today's one-off doses — the list's ALSO TAKEN rows (they count for supply, never adherence). */
  alsoTaken: ExtraDose[];
  /** The due dose marked LAST (the record keeps marks in the order they were made): the dock's at-rest "Last: Vitamin B12 skipped · Morning". Null with nothing recorded. */
  lastMarked: TodayDose | null;
}

const ORDINAL = ['', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th', '10th'];
/** "2nd dose" for a dose key's second time of the day and on; null for its first. */
export const nthOf = (d: Pick<Dose, 'key' | 'itemId'>): string | null => {
  const n = d.key === d.itemId ? 0 : Number(d.key.slice(d.itemId.length + 1));
  return n > 0 ? `${ORDINAL[n] ?? `${n + 1}th`} dose` : null;
};
const ORDER: TimeOfDay[] = TIMES_OF_DAY.map(t => t.id);
const PART: Record<TimeOfDay, string> = Object.fromEntries(TIMES_OF_DAY.map(t => [t.id, t.label])) as Record<TimeOfDay, string>;
/** A dose's clock — none for a plain time of day (the person never set one). */
const clockOf = (d: Dose, fmt: TimeFormat) => (d.time.at === 'slot' ? null : clockText(d.minute, fmt));

/**
 * When an upcoming dose is, in one vocabulary for the readout and the NEXT dock: "Tomorrow 7:00 AM",
 * "Sat 24 · 7:00 AM", "Tomorrow · Morning" (a plain time of day says its part).
 */
export const upcomingWhen = (u: UpcomingDose) => (u.clock ? `${u.day}${u.isTomorrow ? ' ' : ' · '}${u.clock}` : `${u.day} · ${u.part}`);

/** The days after `date` the list is next due (a year ahead at most): its first dose. */
function upcomingAfter(stack: readonly Supplement[], date: string, day: DayTimes, fmt: TimeFormat): UpcomingDose | null {
  const byId = new Map(stack.map(s => [s.id, s]));
  for (let i = 1; i <= 366; i++) {
    const on = shiftYMD(date, i);
    const first = dayDoses(stack, on, day)[0];
    if (!first) continue;
    return {
      date: on, isTomorrow: i === 1, day: i === 1 ? 'Tomorrow' : dayLabel(on, date),
      dose: first, item: byId.get(first.itemId)!, clock: clockOf(first, fmt), part: PART[first.timeOfDay],
    };
  }
  return null;
}

/** TOMORROW: the schedule's doses the day after `date`, by time of day, and who isn't due. */
function buildTomorrow(stack: readonly Supplement[], date: string, day: DayTimes, fmt: TimeFormat): TomorrowModel {
  const tomorrow = shiftYMD(date, 1);
  const byId = new Map(stack.map(s => [s.id, s]));
  const doses = dayDoses(stack, tomorrow, day);
  const lines = TIMES_OF_DAY.flatMap(({ id, label }) => {
    const entries = doses.filter(d => d.timeOfDay === id).map(d => ({ dose: d, item: byId.get(d.itemId)!, when: timePhrase(d.time, fmt), nth: nthOf(d) }));
    return entries.length ? [{ timeOfDay: id, label, entries }] : [];
  });
  const notDue = stack
    .filter(s => s.active && !doses.some(d => d.itemId === s.id))
    .map(s => ({ item: s, how: frequencyLabel(s.frequency), next: nextDueOn(s, shiftYMD(tomorrow, 1)) }));
  return { date: tomorrow, due: doses.length, lines, notDue };
}

/**
 * Today's model. `nowMinute` is the clock (minutes after midnight) — it only decides which
 * compartment reads NOW. `runningLow` is the set of item ids whose supply runs out within a week.
 * `fmt` is the app's 12/24-hour setting (`progressDisplayStore.timeFormat`, passed in — no
 * default, so no caller can forget it); every clock in the model follows it.
 */
export function buildToday(
  stack: readonly Supplement[],
  date: string,
  ids: readonly string[],
  nowMinute: number,
  runningLow: ReadonlySet<string> = new Set(),
  day: DayTimes = DEFAULT_DAY_TIMES,
  fmt: TimeFormat,
): TodayModel {
  const byId = new Map(stack.map(s => [s.id, s]));
  // the day's COUNTED doses (`countedDoses`): a dose marked before its item was paused today stays, as marked
  const doses = countedDoses(stack, date, ids, day);
  const firstOpen = doses.find(d => markOf(ids, d.key) === null)?.key ?? null;
  const rows: TodayDose[] = doses.map(d => {
    const item = byId.get(d.itemId)!;
    return {
      dose: d,
      item,
      mark: markOf(ids, d.key),
      isNext: d.key === firstOpen,
      when: timeLabel(d.time, fmt),
      amount: doseText(item, d.amount),
      note: item.note?.trim() || null,
      nth: nthOf(d),
      shape: pillShapeOf(item),
      runningLow: runningLow.has(item.id),
    };
  });
  const now = timeOfDayOf({ at: 'clock', time: toHHMM(nowMinute) }, day);
  const nowIdx = ORDER.indexOf(now);

  const groups: TodayGroup[] = TIMES_OF_DAY.map(({ id, label }) => {
    const g = rows.filter(r => r.dose.timeOfDay === id);
    const taken = g.filter(r => r.mark === 'taken').length;
    const skipped = g.filter(r => r.mark === 'skipped').length;
    const whens = [...new Set(g.map(r => r.when))];
    const first = g[0]?.dose.minute ?? 0, last = g[g.length - 1]?.dose.minute ?? 0;
    // A plain time of day ("Morning") has no clock the person set — no hint; a clock time is
    // its own hint ("At 7:00 PM", never "At 7:00 PM · 7:00 PM").
    const hint = !g.length || g.every(r => r.dose.time.at === 'slot') ? ''
      : whens.length === 1 ? (g[0].dose.time.at === 'clock' ? whens[0] : `${whens[0]} · ${clockText(first, fmt)}`)
      : clockRange(first, last, fmt);
    return { timeOfDay: id, label, doses: g, taken, skipped, open: g.length - taken - skipped, done: g.length > 0 && taken + skipped === g.length, isNow: id === now, hint };
  });

  const notDue: NotDueItem[] = stack
    .filter(s => s.active && !doses.some(d => d.itemId === s.id))
    .map(s => ({ item: s, how: frequencyLabel(s.frequency), next: nextDueOn(s, shiftYMD(date, 1)) }));

  const alsoTaken: ExtraDose[] = ids
    .filter(k => !k.startsWith(SKIP_PREFIX) && isExtraKey(k))
    .flatMap(key => {
      const item = byId.get(key.slice(0, key.indexOf(EXTRA_MARK)));
      return item ? [{ key, item, shape: pillShapeOf(item), amount: doseText(item, amountOn(item, date)) }] : [];
    });

  const taken = rows.filter(r => r.mark === 'taken').length;
  const skipped = rows.filter(r => r.mark === 'skipped').length;
  const openRows = rows.filter(r => r.mark === null);
  const toTake = openRows.filter(r => ORDER.indexOf(r.dose.timeOfDay) <= nowIdx).length;
  const next = rows.find(r => r.isNext) ?? null;
  let nextTiming: NextTiming | null = null;
  if (next) {
    const idx = ORDER.indexOf(next.dose.timeOfDay);
    const clock = clockOf(next.dose, fmt);
    const fromEarlier = idx < nowIdx;
    nextTiming = {
      clock, beforeItsTime: idx > nowIdx, fromEarlier,
      tag: fromEarlier ? `From this ${next.dose.timeOfDay}` : clock ?? PART[next.dose.timeOfDay],
      phrase: timePhrase(next.dose.time, fmt),
    };
  }
  // The record lists marks in the order they were made (withMark appends): the last one is the latest.
  let lastMarked: TodayDose | null = null;
  let lastAt = -1;
  for (const r of rows) {
    const at = r.mark === null ? -1 : ids.lastIndexOf(r.mark === 'taken' ? r.dose.key : SKIP_PREFIX + r.dose.key);
    if (at > lastAt) { lastAt = at; lastMarked = r; }
  }
  const status: TodayStatus = !rows.length ? 'nothingDue' : openRows.length ? 'open' : 'allRecorded';
  return {
    date, groups, due: rows.length, taken, skipped, open: openRows.length, toTake, later: openRows.length - toTake,
    next, nextTiming, status, upcoming: status === 'open' ? null : upcomingAfter(stack, date, day, fmt),
    tomorrow: buildTomorrow(stack, date, day, fmt),
    now, notDue, paused: stack.filter(s => !s.active), alsoTaken, lastMarked,
  };
}

/** The clock now, in minutes after midnight. */
export const minuteNow = (d: Date = new Date()) => d.getHours() * 60 + d.getMinutes();

// ── The week (the tray's strip; Stats › This week) ───────────────────────────────────────

/** past · today · ahead · before: a past day before the person's record starts (`recordStart`). */
export type WeekDayState = 'past' | 'today' | 'ahead' | 'before';
export interface WeekDay {
  date: string;
  state: WeekDayState;
  /** Doses due. A day before the record counts nothing: 0 — draw it by its state, never as "nothing due". */
  due: number;
  taken: number;
  skipped: number;
  /** Past days: due doses with no record. Today, ahead and before: 0. */
  notRecorded: number;
  /** Days ahead: the items that aren't daily joining that day ("+ Zinc"), in the day's order. Otherwise []. */
  joins: string[];
}

/** A day's own count: what it had due and what was recorded (`dayCountOf`). */
export interface DayCount { date: string; due: number; taken: number; skipped: number; notRecorded: number; toGo: number }

/**
 * ⭐ THE per-day count — ONE rule for every surface that counts a day: the tray's week strip and
 * Today's THIS WEEK (`buildWeek`), the pill box (`buildWeekBox`), Week's BY SUPPLEMENT and the
 * month (sidePanelModel). Before the record (`recordStart`; '' — no floor): nothing. A past day:
 * its row's snapshot (`scheduled`, the count due THEN — no row: the schedule, nothing recorded;
 * ⭐ a pause is dated (`isDueOn` → `pauseOn`), so an item paused since was due on a day before its
 * pause — its miss there is never erased). Today: the live list's COUNTED doses (`countedDoses`:
 * the doses due, and one marked before its item was paused today — a dose taken is a dose taken),
 * and only the marks on THEM (a mark left on a dose since removed isn't one of today's — never
 * "3 of 7 · 1 skipped"). Ahead: the schedule.
 */
export function dayCountOf(
  stack: readonly Supplement[], byDate: ReadonlyMap<string, SupplementDay>, recordStart: string,
  date: string, today: string, day: DayTimes,
): DayCount {
  const zero = { date, due: 0, taken: 0, skipped: 0, notRecorded: 0, toGo: 0 };
  if (date < recordStart && date <= today) return zero;
  const row = byDate.get(date);
  if (date > today) return { ...zero, due: dayDoses(stack, date, day).length };
  if (date === today) {
    const ids = row?.takenIds ?? [];
    const doses = countedDoses(stack, date, ids, day);
    const marks = doses.map(d => markOf(ids, d.key));
    const taken = marks.filter(m => m === 'taken').length;
    const skipped = marks.filter(m => m === 'skipped').length;
    return { ...zero, due: doses.length, taken, skipped, toGo: doses.length - taken - skipped };
  }
  const due = row ? row.scheduled : dayDoses(stack, date, day).length;
  const taken = row ? Math.min(due, takenCount(row.takenIds)) : 0;
  const skipped = row ? Math.min(due - taken, skippedCount(row.takenIds)) : 0;
  return { ...zero, due, taken, skipped, notRecorded: Math.max(0, due - taken - skipped) };
}

/** The names of the NOT-daily items with a dose in `doses` — what joins that day ("+ Zinc"). */
export function joinsOf(stack: readonly Supplement[], doses: readonly Dose[]): string[] {
  const byId = new Map(stack.map(s => [s.id, s]));
  const ids = [...new Set(doses.map(d => d.itemId))];
  return ids.flatMap(id => { const s = byId.get(id); return s && !isDaily(s.frequency) ? [s.name] : []; });
}

/**
 * The Monday-to-Sunday week holding `today`: what each day had due and what was recorded — each
 * day counted by `dayCountOf`, the one rule. `recordStart` (the store's first recorded day): a
 * past day before it is 'before' — never "not recorded", never counted. Without it, no floor (as
 * before the record start was kept).
 */
export function buildWeek(
  stack: readonly Supplement[],
  history: readonly SupplementDay[],
  today: string,
  day: DayTimes = DEFAULT_DAY_TIMES,
  recordStart?: string,
): WeekDay[] {
  const [y, m, d] = today.split('-').map(Number);
  const weekday = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  const monday = shiftYMD(today, -((weekday + 6) % 7));
  const byDate = new Map(history.map(h => [h.date, h]));
  return Array.from({ length: 7 }, (_, i) => {
    const date = shiftYMD(monday, i);
    const state: WeekDayState = date < today ? (recordStart && date < recordStart ? 'before' : 'past') : date === today ? 'today' : 'ahead';
    const { due, taken, skipped, notRecorded } = dayCountOf(stack, byDate, recordStart ?? '', date, today, day);
    return { date, state, due, taken, skipped, notRecorded, joins: state === 'ahead' ? joinsOf(stack, dayDoses(stack, date, day)) : [] };
  });
}

// ── Supply and reviews (Stats) ───────────────────────────────────────────────────────────

export interface SupplyRow {
  item: Supplement; left: number | null; runsOut: string | null; low: boolean;
  /** Units due today (0 on a day it isn't due). */
  perDay: number;
  /** Its rate from the schedule: "2 a day", "1 every 2 days", "1 on Thursdays" — never "None due today". */
  rate: string;
}
export interface ReviewRow { item: Supplement; kind: 'review' | 'change'; date: string; inDays: number; change?: AmountChange; from?: number }

/** Each counted supply: what's left, the day it runs out, and whether it's running low. */
export function buildSupply(
  stack: readonly Supplement[],
  history: readonly SupplementDay[],
  historyFrom: string,
  today: string,
  todayIds: readonly string[],
  day: DayTimes = DEFAULT_DAY_TIMES,
): SupplyRow[] {
  return stack
    .filter(s => s.active && s.supply)
    .map(item => {
      const left = supplyLeft(item, history, historyFrom);
      const runsOut = runsOutOn(item, left, today, todayIds, day);
      const perDay = dayDoses([item], today, day).reduce((n, d) => n + d.amount, 0);
      return { item, left, runsOut, low: isRunningLow(runsOut, today), perDay, rate: rateText(item, today, day) };
    })
    .sort((a, b) => Number(b.low) - Number(a.low) || (a.runsOut ?? '9999').localeCompare(b.runsOut ?? '9999'));
}

/** The ids whose supply runs out within a week (for the list's "Running low"). */
export const lowIds = (rows: readonly SupplyRow[]) => new Set(rows.filter(r => r.low).map(r => r.item.id));

/** A paused item's count, as it stands: nothing is being taken, so it never runs out and is never low. */
export interface HeldSupplyRow { item: Supplement; left: number | null }

/**
 * Each PAUSED item with a count (the side panel when everything is paused — M3 states, "No run-out
 * while paused"): what's left, followed from the record; in the list's order.
 */
export function heldSupply(stack: readonly Supplement[], history: readonly SupplementDay[], historyFrom: string): HeldSupplyRow[] {
  return stack.filter(s => !s.active && s.supply).map(item => ({ item, left: supplyLeft(item, history, historyFrom) }));
}

/**
 * Reviews (due or coming) and the next amount change of each item, soonest first. A paused item's
 * are left out — unless `withPaused` (everything paused: the dates belong to the plan, and apply on resume).
 */
export function buildReviews(stack: readonly Supplement[], today: string, withPaused = false): ReviewRow[] {
  const rows: ReviewRow[] = [];
  for (const item of stack) {
    if (!item.active && !withPaused) continue;
    if (item.reviewDate) rows.push({ item, kind: 'review', date: item.reviewDate, inDays: daysBetween(today, item.reviewDate) });
    const c = nextChange(item, today);
    if (c) rows.push({ item, kind: 'change', date: c.from, inDays: daysBetween(today, c.from), change: c, from: amountOn(item, today) });
  }
  return rows.sort((a, b) => a.date.localeCompare(b.date));
}

/** Items whose review date has come (a quiet notice — nothing stops). */
export const reviewsDue = (stack: readonly Supplement[], today: string) => stack.filter(s => s.active && reviewDue(s, today));
